import React, {
	useEffect,
	useState,
	useRef,
	Fragment,
	useLayoutEffect,
} from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import { MediaToUpload } from '../../model/editableQuestion/model'
import {
	convertTemplateSegmentsToTemplateString,
	MediaObj,
	parseTemplateStringIntoSegments,
	TemplateSegment,
	TemplateSegmentType,
} from '@mission.io/question-toolkit'
import { QuestionsForm } from '@mission.io/question-views'
import ImageModal from '../image-modal/ImageModal'
import ConfirmationModal from '../modal/Confirmation'
import {
	TextImageFieldContainer,
	DeleteIcon,
	ImagePrepend,
} from '../text-image-field/TextImageField'
import 'styled-components/macro'
import { styled } from 'styled-components'
import { generateTemplateDif } from '../../model/editableQuestion/actions'
import { _tempTheme } from '../../utils/tempTheme'

interface TemplateFieldProps {
	setCursorPosition?: (position: number) => void
	value?: string
	media?: MediaObj[] | MediaToUpload[]
	readOnly?: boolean
	onSelectTab?: (index: number) => void
	onChange: (value: QuestionsForm.ChangeTemplateDiff) => void
	onDeleteImage?: () => void
	templateUpdated?: number
	questionIndex?: number
	autoFocus?: boolean
}

/* This component allows you to create a template as a text box
 * where you can add buttons associated with dropdowns
 */
export const TemplateField = (props: TemplateFieldProps): JSX.Element => {
	const {
		setCursorPosition,
		value,
		media,
		readOnly = false,
		onSelectTab,
		onChange,
		onDeleteImage,
		templateUpdated,
		questionIndex = 0,
		autoFocus = false,
	} = props
	const [focused, setFocused] = useState(false)
	const [imageModalIsOpen, setImageModalIsOpen] = useState(false)

	let imageUrl: string | undefined
	if (media && media.length > 0) {
		// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
		imageUrl = media[0].hasOwnProperty('localUrl')
			? (media[0] as MediaToUpload).localUrl
			: // @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
			media[0].hasOwnProperty('url')
			? (media[0] as MediaObj).url
			: undefined
	}
	const handleShowImage = () => {
		if (imageUrl) {
			setImageModalIsOpen(true)
		}
	}
	return (
		<TextImageFieldWrapper $focused={focused}>
			{imageModalIsOpen && (
				<ImageModal
					minimal
					isOpen={imageModalIsOpen}
					setClose={() => setImageModalIsOpen(false)}
					url={imageUrl}
				/>
			)}

			{imageUrl && (
				<ImagePrepend $fixed={true}>
					<img
						src={imageUrl}
						alt="image used to describe question"
						onClick={handleShowImage}
					/>
					<div className="icon-wrap">
						<DeleteIcon
							aria-label="Delete icon"
							role="button"
							onClick={onDeleteImage}
						/>
					</div>
				</ImagePrepend>
			)}

			<ResizableTemplateArea
				setCursorPosition={setCursorPosition}
				value={value}
				className={`${readOnly ? readOnly : ''}`}
				onFocus={() => setFocused(true)}
				focused={focused}
				onBlur={() => setFocused(false)}
				onChange={onChange}
				onSelectTab={onSelectTab}
				templateUpdated={templateUpdated}
				questionIndex={questionIndex}
				autoFocus={autoFocus}
			/>
		</TextImageFieldWrapper>
	)
}

interface ResizableTemplateAreaProps {
	setCursorPosition?: (position: number) => void
	type?: string
	value?: string
	media?: MediaObj[]
	readOnly?: boolean
	className?: string
	templateUpdated?: number
	onSelectTab?: (index: number) => void
	onChange: (value: QuestionsForm.ChangeTemplateDiff) => void
	onBlur: () => void
	onFocus: () => void
	questionIndex: number
	focused?: boolean
	autoFocus?: boolean
}

/**
 * recursivelyTransformHTMLintoTemplate - transverses the html tree and adds template segments to the segment list
 *
 * @param {NodeListOf<ChildNode>} htmlNodes - the html elements to traverse
 * @param {TemplateSegment[]} segments - [MUTATES] the current segments in the template (adds segments to this list)
 */
function recursivelyTransformHTMLintoTemplate(
	htmlNodes: NodeListOf<ChildNode>,
	segments: TemplateSegment[]
) {
	htmlNodes.forEach(node => {
		if (node.nodeType === Node.TEXT_NODE) {
			const text = node.nodeValue
			if (text != null && typeof text === 'string') {
				segments.push({
					type: TemplateSegmentType.Text,
					text: text.replaceAll(
						// replace none breaking space characters with spaces
						String.fromCharCode(160),
						' '
					),
				})
			}
			return
		}
		if (node.nodeType === Node.ELEMENT_NODE) {
			if (node instanceof HTMLElement) {
				// many type of nodes have this attribute,
				const inputId = node.dataset?.inputid
				if (inputId != null && typeof inputId === 'string') {
					segments.push({
						type: TemplateSegmentType.Input,
						inputId,
					})
					return
				}
			}
			if (node.nodeName === 'DIV') {
				segments.push({
					type: TemplateSegmentType.Text,
					text: '\n',
				})
				recursivelyTransformHTMLintoTemplate(node.childNodes, segments)
				return
			}
			if (node.nodeName === 'BR') {
				// chrome uses <div><br/></div> to handle line breaks, so the line break is handled above
				return
			}
			if (node.nodeName === 'SPAN') {
				recursivelyTransformHTMLintoTemplate(node.childNodes, segments)
				return
			}
			if (node.nodeName === 'P') {
				recursivelyTransformHTMLintoTemplate(node.childNodes, segments)
				return
			}
			return
		}
	})
}

/**
 * transformEditableDivToTemplate - get a cloze-dropdown template string out of the editable html div
 *
 * @param {HTMLDivElement} div - the editable div
 *
 * @return {string} - the template string
 */
function transformEditableDivToTemplate(div: HTMLDivElement): string {
	const segments: TemplateSegment[] = []
	recursivelyTransformHTMLintoTemplate(div.childNodes, segments)
	return convertTemplateSegmentsToTemplateString(segments)
}

const ResizableTemplateArea = (
	props: ResizableTemplateAreaProps
): JSX.Element => {
	const {
		setCursorPosition,
		value = '',
		onChange,
		onSelectTab,
		onBlur,
		onFocus,
		templateUpdated,
		questionIndex,
		focused,
		autoFocus = false,
	} = props

	const [
		pendingUpdateAfterDeletionConfirmation,
		setPendingUpdateAfterDeletionConfirmation,
	] = useState<null | QuestionsForm.ChangeTemplateDiff>(null)
	const editableTextBox = useRef<HTMLDivElement>(null)
	const [forceRerenderValue, setForceRerender] = useState(0)
	const foreRerender = () => setForceRerender(value => value + 1)

	const applyUpdate = (update: QuestionsForm.ChangeTemplateDiff) => {
		onChange(update)
	}

	const onChangeContent = () => {
		if (pendingUpdateAfterDeletionConfirmation) {
			// waiting for pending update, so ignore all other updates until that is cleared.
			// force a rerender to keep the states in sync
			foreRerender()
			return
		}
		if (editableTextBox.current) {
			const newTemplate = transformEditableDivToTemplate(
				editableTextBox.current
			)
			const diff = generateTemplateDif(
				parseTemplateStringIntoSegments(value),
				parseTemplateStringIntoSegments(newTemplate)
			)

			if (diff.deletedIds.length) {
				setPendingUpdateAfterDeletionConfirmation(diff)
				// the visualization will differ from the form value since this update is being suspended
				// so force a rerender to get everything back in sync
				foreRerender()
				return
			}

			applyUpdate(diff)
		}
	}

	const createDivContent = (content: string) => {
		if (onSelectTab && editableTextBox.current) {
			editableTextBox.current.innerHTML = buildTemplateHtml(
				content,
				questionIndex
			)

			// added onClick event in each button inside the template
			const listButtons = document.getElementsByClassName('button-dropdown')
			if (listButtons && listButtons.length > 0) {
				for (let button = 0; button < listButtons.length; button++) {
					// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
					listButtons[button].addEventListener(
						'click',
						() => {
							const element = listButtons[button]
							if (element instanceof HTMLElement) {
								const dropdownId = element.dataset.inputid
								if (typeof dropdownId !== 'string') {
									return
								}
								let dropdownNumber = 0
								try {
									dropdownNumber = Number(dropdownId)
								} catch (e) {
									console.error(e)
								}
								onSelectTab(dropdownNumber - 1)
							}
						},
						false
					)
				}
			}
		}
	}

	useEffect(() => {
		if (templateUpdated) createDivContent(value)
	}, [templateUpdated, forceRerenderValue])

	useEffect(() => {
		if (editableTextBox.current) {
			document.addEventListener('keyup', () =>
				updateCursorPosition(editableTextBox.current)
			)
			document.addEventListener('click', () =>
				updateCursorPosition(editableTextBox.current)
			)
			editableTextBox?.current.addEventListener('paste', e =>
				extractResponsePasted(e)
			)
		}
		return () => {
			document.removeEventListener('keyup', () =>
				updateCursorPosition(editableTextBox.current)
			)
			document.removeEventListener('click', () =>
				updateCursorPosition(editableTextBox.current)
			)
			editableTextBox?.current?.removeEventListener('paste', e =>
				extractResponsePasted(e)
			)
		}
	}, [])

	// handle autoFocus
	useLayoutEffect(() => {
		if (editableTextBox.current && autoFocus) {
			editableTextBox.current.focus()
		}
	}, [autoFocus])

	//Function to extract all the words {{response[.*?]}} inside the text pasted
	const extractResponsePasted = (event: any) => {
		event.preventDefault()
		let paste = event.clipboardData.getData('text')

		paste = convertTemplateSegmentsToTemplateString(
			parseTemplateStringIntoSegments(paste).filter(
				({ type }) => type === TemplateSegmentType.Text
			)
		)

		const selection = window.getSelection()

		if (!selection?.rangeCount) {
			return false
		}
		selection?.deleteFromDocument()
		selection?.getRangeAt(0).insertNode(document.createTextNode(paste))

		onChangeContent()

		return false
	}

	const updateCursorPosition = (element: HTMLDivElement | null) => {
		if (element) {
			const substring = getStringUntilCursor(element)
			setCursorPosition && setCursorPosition(substring.length)
		}
	}

	return (
		<ClozeDropdownTextField>
			<Content
				$isFocused={focused}
				$isEmpty={value.length === 0}
				role="textbox"
				contentEditable="true"
				suppressContentEditableWarning={true}
				ref={editableTextBox}
				onFocus={onFocus}
				onBlur={onBlur}
				onInput={onChangeContent}
			/>
			<ConfirmationModal
				isOpen={Boolean(pendingUpdateAfterDeletionConfirmation)}
				confirmText="Delete"
				onCancel={() => setPendingUpdateAfterDeletionConfirmation(null)}
				onConfirm={() => {
					if (pendingUpdateAfterDeletionConfirmation) {
						setPendingUpdateAfterDeletionConfirmation(null)
						applyUpdate(pendingUpdateAfterDeletionConfirmation)
					}
				}}>
				Are you sure you want to delete dropdown
				{(pendingUpdateAfterDeletionConfirmation?.deletedIds.length ?? 0) > 1
					? 's '
					: ' '}
				{pendingUpdateAfterDeletionConfirmation?.deletedIds.map((id, index) => (
					<>
						{index > 0 &&
							index ===
								pendingUpdateAfterDeletionConfirmation.deletedIds.length - 1 &&
							'and'}
						<span
							key={index}
							className="text-base font-semibold bg-brand-horizon-blue text-brand-cloud-blue px-3 mx-1 rounded-lg">
							{id}
						</span>
					</>
				))}
				?
			</ConfirmationModal>
		</ClozeDropdownTextField>
	)
}

/**
 * Function that get the full string unitl cursor Position inside a editablecontent Div element
 * @param {HTMLDivElement} element
 * @return {string} Cursor Position
 */
function getStringUntilCursor(element: HTMLDivElement): string {
	const doc = element.ownerDocument
	const win = doc.defaultView
	let sel: Selection | null

	if (win && !!win.getSelection) {
		sel = win.getSelection()
		if (sel && sel.rangeCount > 0) {
			const range = win.getSelection()?.getRangeAt(0)
			if (range) {
				const preCaretRange = range.cloneRange()
				preCaretRange.selectNodeContents(element)
				preCaretRange.setEnd(range.endContainer, range.endOffset)
				return preCaretRange.toString()
			}
		}
	}
	return ''
}

const EmptySpan = () => {
	return (
		<span
			contentEditable="true"
			style={{
				minWidth: '1px',
				minHeight: '100%',
				lineHeight: '1rem',
			}}
		/>
	)
}

const isEmptySegment = (segment: undefined | TemplateSegment) => {
	return segment
		? segment.type === TemplateSegmentType.Text && segment.text.trim() === ''
		: false
}

const isButtonSegment = (segment: undefined | TemplateSegment) => {
	return segment ? segment.type === TemplateSegmentType.Input : false
}

/**
 * Function that takes a template text for cloze dropdown and build
 * a HTML.
 * @param {string} template
 * @param {number} questionIndex
 * @return {string}
 */
export const buildTemplateHtml = (
	template: string,
	questionIndex: number
): string => {
	const segments = parseTemplateStringIntoSegments(template)

	return renderToStaticMarkup(
		<>
			{segments.map((segment, index) => {
				const key = `${questionIndex}-${index}`
				if (segment.type === TemplateSegmentType.Input) {
					const { inputId } = segment
					const nextIsButtonSegment = isButtonSegment(segments[index + 1])
					return (
						<>
							<button
								id={`${inputId}-${questionIndex}`}
								contentEditable="false"
								suppressContentEditableWarning={true}
								data-inputid={inputId}
								key={key}
								className="button-dropdown mr-1 notranslate">
								{inputId}
							</button>
							{nextIsButtonSegment ? <EmptySpan key={`${key}-empty`} /> : null}
						</>
					)
				}
				if (isEmptySegment(segment)) {
					return <EmptySpan key={key} />
				}
				if (segment.type === TemplateSegmentType.Text) {
					return <Fragment key={key}>{segment.text}</Fragment>
				}
				return null
			})}
		</>
	)
}

const ClozeDropdownTextField = styled.div`
	width: 100%;
	background-color: ${({ theme }) => theme.baseLight};

	button {
		cursor: pointer;
		background-color: ${({ theme }) => theme.primary};
		border: none;
		font-weight: normal;
		font-style: normal;
		font-family: ${({ theme }) => theme.fontFamily};
		font-size: ${_tempTheme.text_sm};
		line-height: ${_tempTheme.text_base};
		text-align: center;
		color: white;
		border-radius: ${_tempTheme.spacing1};
		padding-left: ${_tempTheme.text_xs};
		padding-right: ${_tempTheme.text_xs};
		margin: 0 0.8px;
	}
	button:focus {
		border: 2px solid #2aecbd;
		outline: none !important;
	}
`
const Content = styled.div<{ $isEmpty: boolean; $isFocused?: boolean }>`
	white-space: pre-line;
	color: ${({ theme }) => theme.onBase};
	text-align: start;
	font-size: ${_tempTheme.text_sm};
	font-weight: normal;
	font-style: normal;
	font-family: ${({ theme }) => theme.fontFamily};
	line-height: ${_tempTheme.text_2xl};
	padding: ${_tempTheme.spacing2} 0;
	border: 0;
	box-sizing: content-box;
	background: none;
	margin-left: ${_tempTheme.spacing2};
	display: block;
	min-width: 0;
	animation-name: mui-auto-fill-cancel;
	animation-duration: 10ms;

	button {
		cursor: pointer;
		background-color: ${({ theme }) => theme.primary};
		border: none;
		font-weight: normal;
		font-style: normal;
		font-family: ${({ theme }) => theme.fontFamily};
		font-size: ${_tempTheme.text_sm};
		line-height: ${_tempTheme.text_base};
		text-align: center;
		color: white;
		border-radius: ${_tempTheme.spacing1};
		padding-left: ${_tempTheme.text_xs};
		padding-right: ${_tempTheme.text_xs};
	}
	button:focus {
		border: 2px solid #2aecbd;
	}
	&:focus-visible {
		outline: none;
	}
`

const TextImageFieldWrapper = styled(TextImageFieldContainer)`
	border-top-left-radius: 0 !important;
	border-bottom-left-radius: 0px !important;
	.image-prepend {
		position: relative;
		margin-right: ${_tempTheme.spacing2};
	}
	align-items: center;
`

export default TemplateField
